import * as Yup from 'yup';
import { t } from "i18next";
import moment from "moment";
import { debounce } from "lodash";
import { enqueueSnackbar } from "notistack";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, useFieldArray } from "react-hook-form";
import { useMemo, useState, useEffect, useCallback } from "react";

import { LoadingButton } from "@mui/lab";
import { Box, Stack } from "@mui/system";
import DeleteIcon from '@mui/icons-material/Delete';
import { Chip, Grid, Alert, Button, Dialog, MenuItem, IconButton, Typography, DialogTitle, DialogActions, DialogContent, InputAdornment, CircularProgress } from "@mui/material";

import { useAuthContext } from "src/hooks/auth/use-auth-context";

import { IOrgCandidate } from "src/services/candidates/candidates.types";
import { ICompanyProfile } from "src/services/connections/connections.types";
import { useGetMyCompaniesQuery } from "src/services/connections/connections.service";
import { useGetOrganizationCandidatesQuery } from "src/services/candidates/candidates.service";
import { MeetingType, MeetingStatus, MeetingPlatform } from "src/services/meetings/meetings.types";
import { generateMeetingCreateError, generateMeetingUpdateError } from "src/services/meetings/meetings.utils";
import { useGetMeetingByIdQuery, useCreateMeetingMutation, useUpdateMeetingMutation, useValidateMeetingPlatformMutation } from "src/services/meetings/meetings.service";

import Iconify from "src/components/iconify";
import FeatureLockedListView from "src/components/billing/feature-locked-list-view";
import FormProvider, { RHFSelect, RHFCheckbox, RHFTextField, RHFAutocomplete } from "src/components/hook-form";

import { useFeatures } from "src/sections/billing/usage/hooks/use-features";
import { MeetingStatusLabel } from "src/sections/meetings/components/meeting-status-label";
import { MeetingPlatformIcon } from "src/sections/meetings/components/meeting-platform-icon";

import { FeatureType } from "src/types/subscription.types";

type Props = {
  open: boolean;
  meetingId?: string;
  onClose: () => void;
}

interface IMeetingFormData {
  title: string;
  description?: string;
  type: MeetingType;
  start_at: Date;
  timezone: string;
  duration_minutes?: number;
  participants?: {
    first_name: string;
    last_name: string;
    email: string;
  }[];
  allow_anonymous?: boolean;
  url?: string;
  company_profile: ICompanyProfile | null;
  candidate: IOrgCandidate | null;
}

const DEFAULT_PAGE_OPTIONS = {
  page: 1,
  per_page: 5
};

const MeetingEditorModal = ({
  open,
  meetingId,
  onClose
}: Props) => {

  const authContext = useAuthContext();

  const { currentData: meeting, isLoading } = useGetMeetingByIdQuery(meetingId || '', {
    skip: !meetingId,
  });

  const { canAccess } = useFeatures();

  const [meetingPlatform, setMeetingPlatform] = useState<MeetingPlatform | null>(null);
  const [isPlatformCompatible, setIsPlatformCompatible] = useState<boolean | null>(null);

  const [companySearchQuery, setCompanySearchQuery] = useState<string>('');
  const [candidateSearchQuery, setCandidateSearchQuery] = useState<string>('');
  const [contactSearchQuery, setContactSearchQuery] = useState<string>('');

  const isEditMode = useMemo(() => !!meetingId, [meetingId]);

  const [createMeeting, { isLoading: isCreating }] = useCreateMeetingMutation();
  const [updateMeeting, { isLoading: isUpdating }] = useUpdateMeetingMutation();
  const [validateMeetingPlatform, { isLoading: isValidatingPlatform }] = useValidateMeetingPlatformMutation();

  const { currentData: companies } = useGetMyCompaniesQuery(
    {
      params: {
        ...DEFAULT_PAGE_OPTIONS,
        search_query: companySearchQuery
      },
    }
  );

  const { currentData: candidates } = useGetOrganizationCandidatesQuery(
    {
      page: DEFAULT_PAGE_OPTIONS,
      search_query: candidateSearchQuery
    }
  );

  const AddMeetingSchema = Yup.object().shape({
    title: Yup.string().required(t('validation.required')),
    description: Yup.string().optional(),
    type: Yup.mixed<MeetingType>().required(t('validation.required')),

    start_at: Yup.date().required(t('validation.required')),
    timezone: Yup.string().required(t('validation.required')),

    duration_minutes: Yup.number().min(1, t('validation.min_integer', { min: 0 })).optional()
      .when('type', {
        is: (type: MeetingType) => type === MeetingType.HOSTED,
        then: () => Yup.number().min(1, t('validation.min_integer', { min: 0 })).required(t('validation.required')),
      }),

    participants: Yup.array().of(
      Yup.object().shape({
        first_name: Yup.string().required(t('validation.required')),
        last_name: Yup.string().required(t('validation.required')),
        email: Yup.string().email(t('validation.email_format')).required(t('validation.required')),
      })
    ).max(5, t('validation.max_integer', { max: 5 })).optional()
      .when('type', {
        is: (type: MeetingType) => type === MeetingType.HOSTED,
        then: () => Yup.array().of(
          Yup.object().shape({
            first_name: Yup.string().required(t('validation.required')),
            last_name: Yup.string().required(t('validation.required')),
            email: Yup.string().email(t('validation.email_format')).required(t('validation.required')),
          })
        ).min(1, t('validation.min_integer', { min: 0 })).max(5, t('validation.max_integer', { max: 5 })).required(t('validation.required')),
      }),
    allow_anonymous: Yup.boolean().optional()
      .when('type', {
        is: (type: MeetingType) => type === MeetingType.HOSTED,
        then: () => Yup.boolean().required(t('validation.required')),
      }),
    // trim first: links copied from a Teams/Zoom invite usually carry surrounding whitespace
    url: Yup.string().trim().url(t('validation.url')).optional()
      .when('type', {
        is: (type: MeetingType) => type === MeetingType.EXTERNAL_BOT,
        then: () => Yup.string().trim().url(t('validation.url')).required(t('validation.required')),
      }),
    company_profile: Yup.mixed<ICompanyProfile>().nullable().default(null),
    candidate: Yup.mixed<IOrgCandidate>().nullable().default(null),
  });

  const methods = useForm<IMeetingFormData>({
    resolver: yupResolver(AddMeetingSchema),
    defaultValues: {
      title: '',
      description: '',
      type: MeetingType.EXTERNAL_BOT,
      start_at: new Date(),
      timezone: moment.tz.guess(),
      duration_minutes: 30,
      participants: [],
      allow_anonymous: false,
      url: '',
      company_profile: null,
      candidate: null,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    clearErrors,
    formState: { isValid, isSubmitting, errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'participants',
  });

  const meetingType = watch('type');
  const meetingUrl = watch('url');

  useEffect(() => {
    if (open && meeting) {
      const timezone = meeting.timezone || moment.tz.guess();
      const startAt = moment.utc(meeting.start_at).tz(timezone).format('YYYY-MM-DDTHH:mm');

      reset({
        title: meeting.title,
        description: meeting.description || '',
        type: meeting.type,
        start_at: startAt as Date | any,
        timezone,
        duration_minutes: meeting.duration_minutes || 30,
        participants: meeting.participants || [],
        allow_anonymous: meeting.allow_anonymous || false,
        url: meeting.url || '',
        company_profile: meeting.related_company ? meeting.related_company : null,
        candidate: meeting.related_candidate ? meeting.related_candidate : null,
      });
    }
  }, [open, meeting, reset]);


  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async (data: IMeetingFormData) => {

    try {

      const startMoment = moment.tz(data.start_at, data.timezone);
      const utcStart = startMoment.utc().format('YYYY-MM-DDTHH:mm:ss');

      const meetingData = {
        title: data.title,
        description: data.description || '',
        type: data.type,
        start_at: utcStart,
        timezone: data.timezone,
        duration_minutes: data.duration_minutes,
        allow_anonymous: data.allow_anonymous,
        url: data.url || '',
        participants: Array.isArray(data.participants) ? data.participants.map(p => ({
          first_name: p.first_name,
          last_name: p.last_name,
          email: p.email
        })) : [],
        related_company_id: data.company_profile ? data.company_profile.id : null,
        related_candidate_id: data.candidate ? data.candidate.id : null,
      }

      if (isEditMode) {

        if (!meetingId) {
          return;
        }

        await updateMeeting({
          id: meetingId,
          data: meetingData
        }).unwrap();

      } else {
        await createMeeting(meetingData).unwrap();
      }

      if (isEditMode) {
        enqueueSnackbar(t('meetings.api.update.success'), {
          variant: 'success',
        });
      }
      else {
        enqueueSnackbar(t('meetings.api.create.success'), {
          variant: 'success',
        });
      }

      handleClose();
    }
    catch (error) {

      if (isEditMode) {
        enqueueSnackbar(generateMeetingUpdateError(error), {
          variant: 'error',
        });
      }
      else {
        enqueueSnackbar(generateMeetingCreateError(error), {
          variant: 'error',
        });
      }

    }
  }, [isEditMode, meetingId, createMeeting, updateMeeting, handleClose]);

  const title = useMemo(() => {
    if (isEditMode) {
      return t('meetings.editor.edit_title');
    }

    return t('meetings.editor.title');
  }, [isEditMode]);

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setCompanySearchQuery(value);
    }, 500),
    [setCompanySearchQuery]
  );

  const validateMeetingLink = useCallback(async (rawUrl: string) => {
    const url = rawUrl.trim();
    setIsPlatformCompatible(null);
    setMeetingPlatform(null);
    clearErrors('url');

    if (!url) {
      setIsPlatformCompatible(null);
      return;
    }

    // check if valid url
    if (!/^https?:\/\/.+/i.test(url)) {
      return;
    }

    try {
      const response = await validateMeetingPlatform({ url }).unwrap();
      if (response.supported) {
        setMeetingPlatform(response.platform as MeetingPlatform);
        setIsPlatformCompatible(response.supported);
      }
      else {
        setMeetingPlatform(null);
        setIsPlatformCompatible(false);
        setError('url', {
          type: 'manual',
          message: t('meetings.editor.form.url.unsupported_platform')
        });
      }

    } catch (error) {

      setMeetingPlatform(null);
      setIsPlatformCompatible(false);

    }
  }, [validateMeetingPlatform, setMeetingPlatform, setIsPlatformCompatible, clearErrors, setError]);

  const debouncedValidateMeetingLink = useMemo(
    () => debounce(validateMeetingLink, 500),
    [validateMeetingLink]
  );

  useEffect(() => {
    // call the debounced function when meetingUrl changes
    if (meetingUrl) {
      debouncedValidateMeetingLink(meetingUrl);
    } else {
      setIsPlatformCompatible(null);
      setMeetingPlatform(null);
    }

    return () => {
      debouncedValidateMeetingLink.cancel();
    };
  }, [meetingUrl, debouncedValidateMeetingLink]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {title}
          </Typography>

          {
            meeting && (
              <MeetingStatusLabel status={meeting.status} />
            )
          }

        </Stack>
      </DialogTitle>

      {
        canAccess(FeatureType.MEETINGS_ENABLED) ? (
          <FormProvider methods={methods} onSubmit={handleSubmit(handleSave)}>
            <DialogContent>
              <Grid container spacing={2} pt={2}>
                <Grid item xs={12}>
                  <RHFTextField
                    name="title"
                    label={t('meetings.editor.form.title.label')}
                    placeholder={t('meetings.editor.form.title.placeholder')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <RHFTextField
                    name="description"
                    label={t('meetings.editor.form.description.label')}
                    placeholder={t('meetings.editor.form.description.placeholder')}
                    fullWidth
                    multiline
                    rows={4}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="column" spacing={1}>
                    <RHFTextField
                      name="type"
                      disabled={isEditMode}
                      label={t('meetings.editor.form.type.label')}
                      placeholder={t('meetings.editor.form.type.placeholder')}
                      select
                      fullWidth
                    >
                      <MenuItem key={MeetingType.EXTERNAL_BOT} value={MeetingType.EXTERNAL_BOT}>
                        <Stack
                          width="100%"
                          direction="row"
                          spacing={1}
                          display="flex"
                          justifyContent="space-between"
                        >
                          {t(`meetings.editor.form.type.options.${MeetingType.EXTERNAL_BOT}`)}
                        </Stack>
                      </MenuItem>
                      <MenuItem key={MeetingType.HOSTED} value={MeetingType.HOSTED} disabled>
                        <Stack
                          width="100%"
                          direction="row"
                          spacing={1}
                          display="flex"
                          justifyContent="space-between"
                        >
                          {t(`meetings.editor.form.type.options.${MeetingType.HOSTED}`)}
                          <Chip
                            label={t('common.coming_soon')}
                            color="error"
                            variant="filled"
                            size="small"
                          />
                        </Stack>
                      </MenuItem>
                    </RHFTextField>
                    {
                      meetingType === MeetingType.EXTERNAL_BOT && (
                        <Alert severity="info" variant="outlined">
                          {t('meetings.editor.form.type.external_bot_info')}
                        </Alert>
                      )
                    }
                    {
                      meetingType === MeetingType.HOSTED && (
                        <Alert severity="info" variant="outlined">
                          {t('meetings.editor.form.type.hosted_info')}
                        </Alert>
                      )
                    }
                  </Stack>
                </Grid>

                {
                  meetingType === MeetingType.EXTERNAL_BOT && (
                    <Grid item xs={12}>
                      <RHFTextField
                        name="url"
                        disabled={isEditMode}
                        label={t('meetings.editor.form.url.label')}
                        placeholder={t('meetings.editor.form.url.placeholder')}
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">

                              {
                                isValidatingPlatform && (
                                  <CircularProgress size={20} />
                                )
                              }

                              {
                                (!isValidatingPlatform && meetingPlatform) && (
                                  <MeetingPlatformIcon platform={meetingPlatform} />
                                )
                              }

                              {
                                (!isValidatingPlatform && isPlatformCompatible === false) && (
                                  <Iconify
                                    icon="codex:warning"
                                    color="error.main"
                                    width={20}
                                  />
                                )
                              }

                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                  )
                }

                <Grid item xs={12}>
                  <RHFTextField
                    name="start_at"
                    label={t('meetings.editor.form.start_at.label')}
                    type="datetime-local"
                    disabled={isEditMode && meeting?.status !== MeetingStatus.SCHEDULED}
                    
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <RHFSelect
                    name="timezone"
                    disabled={isEditMode && meeting?.status !== MeetingStatus.SCHEDULED}
                    label={t('meetings.editor.form.timezone.label')}
                    placeholder={t('meetings.editor.form.timezone.label')}
                  >
                    {moment.tz.names().map((timezone) => (
                      <MenuItem key={timezone} value={timezone}>
                        {timezone}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                </Grid>

                <Grid item xs={12}>
                  <RHFTextField
                    name="duration_minutes"
                    label={t('meetings.editor.form.duration_minutes.label')}
                    disabled={isEditMode && meeting?.status !== MeetingStatus.SCHEDULED}
                    type="number"
                    placeholder={t('meetings.editor.form.duration_minutes.placeholder')}
                    fullWidth
                  />
                </Grid>

                {
                  meetingType === MeetingType.HOSTED && (
                    <Grid item xs={12}>
                      <RHFCheckbox
                        name="allow_anonymous"
                        disabled={isEditMode && meeting?.status !== MeetingStatus.SCHEDULED}
                        label={t('meetings.editor.form.allow_anonymous.label')}
                        helperText={t('meetings.editor.form.allow_anonymous.tooltip')}
                      />
                    </Grid>
                  )
                }

                {
                  meetingType === MeetingType.HOSTED && (
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1} mb={2} px={1} alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle2">
                          {t('meetings.editor.form.participants.label')}
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={<Iconify icon="eva:plus-fill" />}
                          disabled={isEditMode && meeting?.status !== MeetingStatus.SCHEDULED}
                          onClick={() => append({ first_name: '', last_name: '', email: '' })}
                        >
                          {t('meetings.editor.form.participants.add')}
                        </Button>
                      </Stack>
                      {fields.length > 0 ? (
                        fields.map((field, index) => (
                          <Grid container spacing={2} key={field.id} sx={{ mb: 2, pl: 1 }}>
                            <Grid item xs={12} sm={5}>
                              <RHFTextField
                                name={`participants.${index}.email`}
                                label={t('common.form_labels.email')}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <RHFTextField
                                name={`participants.${index}.first_name`}
                                label={t('common.form_labels.first_name')}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <RHFTextField
                                name={`participants.${index}.last_name`}
                                label={t('common.form_labels.last_name')}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={1}>
                              <IconButton onClick={() => remove(index)} color="error" disabled={isEditMode && meeting?.status !== MeetingStatus.SCHEDULED}>
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        ))
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                            {t('meetings.editor.form.participants.empty')}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  )
                }

                <Grid item xs={12}>
                  <RHFAutocomplete
                    multiple={false}
                    fullWidth
                    onInputChange={(_e, value) => onSearchQueryChange(value)}
                    name="company_profile"
                    label={t('meetings.editor.form.related_company.label')}
                    placeholder={t('meetings.editor.form.related_company.placeholder')}
                    options={companies?.results as ICompanyProfile[] ?? []}
                    filterOptions={(x) => x}
                    renderOption={({ key, ...rest }, option: ICompanyProfile) => (
                      <li key={key} {...rest}>
                        <Stack direction="column">
                          <Typography variant="body1">
                            {`${option.company_name}`}
                          </Typography>
                        </Stack>
                      </li>
                    )}
                    getOptionLabel={(option) => (option as ICompanyProfile).company_name as string}
                    isOptionEqualToValue={(option, value) =>
                      option && (option as ICompanyProfile).id === (value as ICompanyProfile).id
                    }
                    getOptionKey={(option) => (option as ICompanyProfile).id as string}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RHFAutocomplete
                    multiple={false}
                    fullWidth
                    onInputChange={(_e, value) => setCandidateSearchQuery(value)}
                    name="candidate"
                    label={t('meetings.editor.form.related_candidate.label')}
                    placeholder={t('meetings.editor.form.related_candidate.placeholder')}
                    options={candidates?.results as IOrgCandidate[] ?? []}
                    filterOptions={(x) => x}
                    renderOption={({ key, ...rest }, option: IOrgCandidate) => (
                      <li key={key} {...rest}>
                        <Stack direction="column">
                          <Typography variant="body1">
                            {`${option.candidate_profile.first_name} ${option.candidate_profile.last_name}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.email}
                          </Typography>
                        </Stack>
                      </li>
                    )}
                    getOptionLabel={(option) => `${(option as IOrgCandidate).candidate_profile.first_name} ${(option as IOrgCandidate).candidate_profile.last_name}`}
                    isOptionEqualToValue={(option, value) =>
                      option && (option as IOrgCandidate).id === (value as IOrgCandidate).id
                    }
                    getOptionKey={(option) => (option as IOrgCandidate).id as string}
                  />
                </Grid>

              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>{t('common.cancel')}</Button>

              <LoadingButton
                type="submit"
                variant="contained"
                loading={isCreating || isUpdating}
              >{t('common.save')}
              </LoadingButton>

            </DialogActions>
          </FormProvider>
        )
          :
          (
            <Box sx={{ p: 2, pb: 8 }}>
              <FeatureLockedListView text={t('billing.subscription_required')} onNavigate={onClose} />
            </Box>
          )
      }

    </Dialog>
  );
}

export default MeetingEditorModal;